import {
    LiveMatchListActions,
    LiveMatchListSetFilterDataAction,
    LiveGroupsMatchListSetFilterDataAction,
    LiveGroupsOverviewMatchListSetFilterDataAction,
    LiveMatchListSetLoadingData,
    LiveMatchListStoreModel,
    LiveMatchListTopicModel,
} from './live-match-list.constants';
import { AppDispatch, AppThunk, GetState } from '@om-fe/core/core/types';
import {
    apiSubscribeAction,
    apiUnsubscribeAction,
    attachReducerAction,
    detachReducerAction,
} from '@om-fe/core/utils/action';
import {
    getLiveMatchListSliceName,
    getLiveGroupsMatchListSliceName,
    getLiveMatchListTopic,
    getLiveGroupsMatchListTopic,
    getLiveGroupsOverviewMatchListTopic,
} from './live-match-list.utils';
import { 
    liveMatchListReducerCreator,
    liveGroupsMatchListReducerCreator,
    liveGroupsOverviewMatchListReducerCreator,
} from './live-match-list.reducer';
import { filterNullParams } from '@om-fe/core/utils/router';
import getSlice from '@om-fe/core/utils/get-slice';
import shallowEqual from 'shallowequal';

export const liveMatchListSetLoadingData = (slice: string, isLoadingMoreData: boolean = false): LiveMatchListSetLoadingData => ({
    type: LiveMatchListActions.SetLoadingData,
    meta: {
        slice,
    },
    payload: {
        isLoadingMoreData,
    },
});

export const liveMatchListSetFilterData = (
    slice: string,
    limit: number,
    disciplineId: string,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    isAuthenForTopic?: boolean,
): LiveMatchListSetFilterDataAction => ({
    type: LiveMatchListActions.SetFilterData,
    meta: {
        slice,
    },
    payload: {
        limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        isAuthenForTopic,
    },
});

export const liveGroupsMatchListSetFilterData = (
    slice: string,
    limit: number,
    disciplineId: string,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    marketGroupsOverviewNumber: number | undefined,
    isAuthenForTopic?: boolean,
): LiveGroupsMatchListSetFilterDataAction => ({
    type: LiveMatchListActions.SetFilterData,
    meta: {
        slice,
    },
    payload: {
        limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        marketGroupsOverviewNumber,
        isAuthenForTopic,
    },
});

export const liveGroupsOverviewMatchListSetFilterData = (
    slice: string,
    limit: number,
    disciplineId: string,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    marketGroupsOverviewIds: string | undefined,
    isAuthenForTopic?: boolean,
): LiveGroupsOverviewMatchListSetFilterDataAction => ({
    type: LiveMatchListActions.SetFilterData,
    meta: {
        slice,
    },
    payload: {
        limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        marketGroupsOverviewIds,
        isAuthenForTopic,
    },
});

export const getLiveMatchList = (
    instanceId: string,
    disciplineId: string,
    limit: number,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    isAuthenForTopic?: boolean,
) => (dispatch: AppDispatch) => {
    const slice: string = getLiveMatchListSliceName(instanceId);
    const topic: LiveMatchListTopicModel = getLiveMatchListTopic(
        disciplineId,
        limit,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        undefined,
        undefined,
        isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    dispatch(attachReducerAction(slice, liveMatchListReducerCreator(instanceId)));

    dispatch(liveMatchListSetFilterData(
        slice,
        limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        isAuthenForTopic,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const getLiveGroupsMatchList = (
    instanceId: string,
    disciplineId: string,
    limit: number,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    marketGroupsOverviewNumber: number | undefined,
    isAuthenForTopic?: boolean,
) => (dispatch: AppDispatch) => {
    const slice: string = getLiveGroupsMatchListSliceName(instanceId);
    const topic: LiveMatchListTopicModel = getLiveGroupsMatchListTopic(
        disciplineId,
        limit,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        undefined,
        undefined,
        marketGroupsOverviewNumber,
        isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    dispatch(attachReducerAction(slice, liveGroupsMatchListReducerCreator(instanceId)));

    dispatch(liveGroupsMatchListSetFilterData(
        slice,
        limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        marketGroupsOverviewNumber,
        isAuthenForTopic,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const getLiveGroupsOverviewMatchList = (
    instanceId: string,
    disciplineId: string,
    limit: number,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    marketGroupsOverviewIds: string | undefined,
    isAuthenForTopic?: boolean,
) => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getLiveMatchListSliceName(instanceId);
    const matchListState: LiveMatchListStoreModel | undefined = getSlice<LiveMatchListStoreModel>(getState(), slice);

    const topic: LiveMatchListTopicModel = getLiveGroupsOverviewMatchListTopic(
        disciplineId,
        matchListState?.limit || limit,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        marketGroupsOverviewIds,
        undefined,
        undefined,
        isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    dispatch(attachReducerAction(slice, liveGroupsOverviewMatchListReducerCreator(instanceId)));

    dispatch(liveGroupsOverviewMatchListSetFilterData(
        slice,
        matchListState?.limit || limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        marketGroupsOverviewIds,
        isAuthenForTopic,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeLiveMatchList = (
    instanceId: string,
): AppThunk => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getLiveMatchListSliceName(instanceId);
    const matchListState: LiveMatchListStoreModel | undefined = getSlice<LiveMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const topic: LiveMatchListTopicModel = getLiveMatchListTopic(
        matchListState.disciplineId!,
        matchListState.limit,
        matchListState.locationId,
        matchListState.isDefault,
        matchListState.mainMarketsLimit,
        matchListState.eventCategoryId,
        undefined,
        undefined,
        matchListState.isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    dispatch(detachReducerAction(slice));
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeLiveGroupsMatchList = (
    instanceId: string,
): AppThunk => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getLiveGroupsMatchListSliceName(instanceId);
    const matchListState: LiveMatchListStoreModel | undefined = getSlice<LiveMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const topic: LiveMatchListTopicModel = getLiveGroupsMatchListTopic(
        matchListState.disciplineId!,
        matchListState.limit,
        matchListState.locationId,
        matchListState.isDefault,
        matchListState.mainMarketsLimit,
        matchListState.eventCategoryId,
        matchListState.isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    dispatch(detachReducerAction(slice));
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeLiveGroupsOverviewMatchList = (
    instanceId: string,
    noDetach?: boolean,
): AppThunk => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getLiveMatchListSliceName(instanceId);
    const matchListState: LiveMatchListStoreModel | undefined = getSlice<LiveMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const topic: LiveMatchListTopicModel = getLiveGroupsOverviewMatchListTopic(
        matchListState.disciplineId!,
        matchListState.limit,
        matchListState.locationId,
        matchListState.isDefault,
        matchListState.mainMarketsLimit,
        matchListState.eventCategoryId,
        matchListState.marketGroupsOverviewIds,
        undefined,
        undefined,
        matchListState.isAuthenForTopic,
    ) as LiveMatchListTopicModel;
        
    if (!noDetach) {
        dispatch(detachReducerAction(slice));
    }
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const updateLiveMatchList = (
    instanceId: string,
    disciplineId: string,
    limit: number,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    isAuthenForTopic?: boolean,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getLiveMatchListSliceName(instanceId);

    const matchListState: LiveMatchListStoreModel | undefined = getSlice<LiveMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const oldTopic: LiveMatchListTopicModel = getLiveMatchListTopic(
        matchListState.disciplineId!,
        matchListState.limit,
        matchListState.locationId,
        matchListState.isDefault,
        matchListState.mainMarketsLimit,
        matchListState.eventCategoryId,
        undefined,
        undefined,
        matchListState.isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    const newTopic: LiveMatchListTopicModel = getLiveMatchListTopic(
        disciplineId,
        limit,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        undefined,
        undefined,
        isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    const isSameMatchList: boolean = shallowEqual(
        [matchListState.disciplineId!, matchListState.locationId, matchListState.isDefault, matchListState.mainMarketsLimit, matchListState.eventCategoryId, matchListState.isAuthenForTopic],
        [disciplineId, locationId, isDefault, mainMarketsLimit, eventCategoryId, isAuthenForTopic],
    );
    dispatch(liveMatchListSetLoadingData(slice, isSameMatchList));

    dispatch(liveMatchListSetFilterData(
        slice,
        limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        isAuthenForTopic,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};

export const updateLiveGroupsMatchList = (
    instanceId: string,
    disciplineId: string,
    limit: number,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    marketGroupsOverviewNumber: number | undefined,
    isAuthenForTopic?: boolean,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getLiveGroupsMatchListSliceName(instanceId);

    const matchListState: LiveMatchListStoreModel | undefined = getSlice<LiveMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const oldTopic: LiveMatchListTopicModel = getLiveGroupsMatchListTopic(
        matchListState.disciplineId!,
        matchListState.limit,
        matchListState.locationId,
        matchListState.isDefault,
        matchListState.mainMarketsLimit,
        matchListState.eventCategoryId,
        undefined,
        undefined,
        matchListState.marketGroupsOverviewNumber,
        matchListState.isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    const newTopic: LiveMatchListTopicModel = getLiveGroupsMatchListTopic(
        disciplineId,
        limit,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        undefined,
        undefined,
        marketGroupsOverviewNumber,
        isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    const isSameMatchList: boolean = shallowEqual(
        [ matchListState.disciplineId!, matchListState.locationId, matchListState.isDefault, matchListState.mainMarketsLimit, matchListState.eventCategoryId, matchListState.isAuthenForTopic],
        [ disciplineId, locationId, isDefault, mainMarketsLimit, eventCategoryId, isAuthenForTopic],
    );
    dispatch(liveMatchListSetLoadingData(slice, isSameMatchList));

    dispatch(liveGroupsMatchListSetFilterData(
        slice,
        limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        marketGroupsOverviewNumber,
        isAuthenForTopic,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};

export const updateLiveGroupsOverviewMatchList = (
    instanceId: string,
    disciplineId: string,
    limit: number,
    locationId: string | undefined,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
    eventCategoryId: string | undefined,
    marketGroupsOverviewIds: string | undefined,
    isAuthenForTopic?: boolean,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getLiveMatchListSliceName(instanceId);

    const matchListState: LiveMatchListStoreModel | undefined = getSlice<LiveMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const oldTopic: LiveMatchListTopicModel = getLiveGroupsOverviewMatchListTopic(
        matchListState.disciplineId!,
        matchListState.limit,
        matchListState.locationId,
        matchListState.isDefault,
        matchListState.mainMarketsLimit,
        matchListState.eventCategoryId,
        matchListState.marketGroupsOverviewIds,
        undefined,
        undefined,
        matchListState.isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    const newTopic: LiveMatchListTopicModel = getLiveGroupsOverviewMatchListTopic(
        disciplineId,
        limit,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        marketGroupsOverviewIds,
        undefined,
        undefined,
        isAuthenForTopic,
    ) as LiveMatchListTopicModel;

    const isSameMatchList: boolean = shallowEqual(
        [ matchListState.disciplineId!, matchListState.locationId, matchListState.isDefault, matchListState.mainMarketsLimit, matchListState.eventCategoryId, matchListState.isAuthenForTopic],
        [ disciplineId, locationId, isDefault, mainMarketsLimit, eventCategoryId, isAuthenForTopic],
    );
    dispatch(liveMatchListSetLoadingData(slice, isSameMatchList));

    dispatch(liveGroupsOverviewMatchListSetFilterData(
        slice,
        limit,
        disciplineId,
        locationId,
        isDefault,
        mainMarketsLimit,
        eventCategoryId,
        marketGroupsOverviewIds,
        isAuthenForTopic,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};

export const addMoreLiveMatchList = (instanceId: string, limitNumberOfEvents: number, isAuthenForTopic?: boolean,
): AppThunk => (
    dispatch: AppDispatch, getState: GetState,
): void => {

    const slice: string = getLiveMatchListSliceName(instanceId);

    const matchListState: LiveMatchListStoreModel | undefined = getSlice<LiveMatchListStoreModel>(getState(), slice);

    if (!matchListState) {
        return;
    }

    const { limit, disciplineId, locationId, isDefault, mainMarketsLimit, eventCategoryId, filteredMarketGroupsOverviewList } = matchListState;

    const newLimit: number = limit + limitNumberOfEvents;
    if (filteredMarketGroupsOverviewList.length) {
        const { limit } = matchListState;

        const newEventsNumber = limit + limitNumberOfEvents;
        const groupOverviewsIds = filteredMarketGroupsOverviewList.join(',');
        dispatch(updateLiveGroupsOverviewMatchList(instanceId, disciplineId!, newEventsNumber, locationId, isDefault, mainMarketsLimit, eventCategoryId, groupOverviewsIds, isAuthenForTopic));
    } else {
        dispatch(updateLiveMatchList(instanceId, disciplineId!, newLimit, locationId, isDefault, mainMarketsLimit, eventCategoryId, isAuthenForTopic));
    }
};
